import React, { useMemo, useState, useEffect, useCallback } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer1";
import ContactModal from "../../components/Common/ContactModal";

const DatatableTables = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [search, setSearch] = useState("");
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    if (!user || !token) return;

    try {
      setLoading(true);
      const params = {
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      };

      let apiUrl = "https://api.kunalsdrumclass.com/api/GetAllContacts";

      const query = new URLSearchParams(params).toString();
      const res = await fetch(`${apiUrl}?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading contact enquiries:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (data = {}) => {
    setFormData(data);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({});
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "First Name",
        meta: {
          sticky: true,
        },
        accessorKey: "FirstName",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Last Name",
        accessorKey: "LastName",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Email",
        accessorKey: "Email",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Contact No",
        accessorKey: "Subject",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const mobile = rowData.Subject;
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{rowData.Subject}</span>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3"></i>
                </a>
                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3"></i>
                </a>
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3"></i>
                </a>

              </div>
            </div>
          );
        },
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY"),
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <div className="d-flex gap-2">
              {hasPermission(permissionMap, MODULES.CONTACTENQUIRIES, PERMISSIONS.VIEW) && (
                <button className="btn btn-sm btn-primary" onClick={() => openModal(rowData)}>View</button>
              )}
              {hasPermission(permissionMap, MODULES.CONTACTENQUIRIES, PERMISSIONS.DELETE) && (
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(rowData)}>Delete</button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this enquiry?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteContactById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `enquiry deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  //meta title
  document.title = "Contact Enquiries | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Contact Enquiries"
          breadcrumbItems={[
            { title: "Contact Enquiries" }
          ]}
        />

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <ContactModal
          show={showModal}
          onClose={closeModal}
          data={formData}
        />
      </div>
    </div>
  );
};

export default DatatableTables;
